// Content for "Astrology & Bhakti" series, Part 1
// Class by HG Balarāma Śakti Dāsa, Bhakti Saṅgha, on SB 5.14.8 (30 October 2021)
//
// Sections 1 and 2 below reproduce the class's own PPT slides as closely as possible
// (light grammar smoothing of bullet fragments only). The recorded audio is used only to
// build the table and diagram below. No meta-commentary about the class, slides, or
// sourcing/attribution is shown to the reader. For maintainers: Section 1's dialogue is
// Śrīla Viśvanātha Cakravartī Ṭhākura's commentary on SB 1.5.18, labeled "SVCT" on the
// slides.

import type { TopicContent } from '../types';

const content: TopicContent = {
    primarySlokaHeader: 'Śrīmad-Bhāgavatam 1.5.18',
    primarySloka: {
        reference: 'Śrīmad-Bhāgavatam 1.5.18',
        sloka: `tasyaiva hetuḥ prayateta kovido
na labhyate yad bhramatām upary adhaḥ
tal labhyate duḥkhavad anyataḥ sukhaṁ
kālena sarvatra gabhīra-raṁhasā`,
        translation:
            'The wise man (<strong>kovidaḥ</strong>) strives (<strong>prayateta</strong>) for that happiness (<strong>tasya eva hetoḥ</strong>) which is not attained (<strong>na labhyate</strong>) by jīvas wandering (<strong>yad bhramatām</strong>) from Brahma-loka to the lowest species (<strong>upary adhaḥ</strong>). Material happiness (<strong>tad sukhaṁ</strong>) is attained in all cases (<strong>sarvatra labhyate</strong>) without endeavor, by the law of karma (<strong>anyataḥ</strong>), just like distress (<strong>duḥkhavad</strong>), through the force of fast-moving time (<strong>gabhīra-raṁhasā kālena</strong>).',
    },
    blocks: [
        {
            type: 'divider',
            dividerLabel: 'Section 1',
        },
        {
            type: 'heading',
            headingText: 'Section 1 — What Does Śrīmad-Bhāgavatam Seem to Recommend for Devotees?',
        },
        {
            type: 'dialogue',
            objection:
                'But the śrutis say <em>karmaṇā pitṛ-lokaḥ</em> — by karma one goes to Pitṛ-loka (Bṛhad Āraṇyaka Upaniṣad 1.5); and <em>āpām somam amṛtā abhūma</em> — we drank soma and became immortal (Ṛg Veda 8.48.3). These encourage people to seek happiness of Svarga in the next life. The mercantile people encourage others to seek happiness in this life. How can one promote bhakti for people by rejecting one\'s dharma and consequently the happiness in this and the next life?',
            response:
                'True, but the wise are not deluded by this at all — that is expressed in this verse. A person with discrimination (<em>kovidaḥ</em>) should endeavor for that cause which is not attained by the jīvas wandering up to Brahma-loka or down to non-moving bodies. But material happiness there is attained without endeavor (<em>anyataḥ</em>), due to ancient karmas — even being born as pigs, or being born in hell — just as distress is also attained without endeavor (<em>duḥkhavat</em>).',
        },
        {
            type: 'text',
            content:
                'Continuing the same commentary: it is said, <em>aprārthitāni duḥkhāni yathaivāyānti dehinām, sukhāny api tathā manye dainyam atrātiricyate</em> — just as one does not pray for misery, and it nevertheless comes to all creatures, in the same way happiness predominates over suffering.',
        },
        {
            type: 'divider',
            dividerLabel: 'Section 2',
        },
        {
            type: 'heading',
            headingText: 'Section 2 — Does It Mean Everything Is Predestined?',
        },
        {
            type: 'sloka',
            sloka: {
                reference: 'Bhagavad-gītā 18.14',
                sloka: `adhiṣṭhānaṁ tathā kartā
karaṇaṁ ca pṛthag-vidham
vividhāś ca pṛthak ceṣṭā
daivaṁ caivātra pañcamam`,
                translation:
                    'These five factors are (<strong>atra pañcamam</strong>) the body, the false ego (<strong>adhiṣṭhānaṁ tathā kartā</strong>), the senses of various types (<strong>karaṇaṁ ca pṛthag-vidham</strong>), the life airs with various functions (<strong>vividhāś ca pṛthak ceṣṭā</strong>), and the Supersoul (<strong>daivaṁ</strong>).',
            },
        },
        {
            type: 'table',
            table: {
                label: 'The Five Factors of Action (Bhagavad-gītā 18.14)',
                headers: ['Factor', 'Meaning'],
                rows: [
                    ['Adhiṣṭhānam', 'The body, or the place where an activity is performed.'],
                    ['Kartā', 'The false ego (ahaṅkāra) — one\'s self-identification, or upādhi.'],
                    ['Karaṇam', 'The senses of various types — the faculties of seeing, hearing, lifting, and walking.'],
                    ['Ceṣṭā', 'Endeavor.'],
                    [
                        'Daivam',
                        'Destiny — the Supreme Lord within (antaryāmī), who dispenses the results of karma. For a surrendered devotee, this factor becomes <em>anugraha</em>, the Lord\'s personal mercy (see Part 2).',
                    ],
                ],
            },
        },
        {
            type: 'diagram',
            diagram: {
                mermaidCode: `flowchart TD
    A["Any endeavor"] --> B{"Material activity\\nor bhakti?"}
    B -->|"Material activity"| C["Outcome = mix of ceṣṭā + daivam"]
    C --> D["The ratio varies by activity —\\nfrom fully predestined\\nto strongly effort-dependent"]
    D --> E["Investing heavily is a gamble"]
    B -->|"Bhakti"| F["Endeavor itself is the result"]
    F --> G["Every act of devotional service\\nis guaranteed spiritual advancement"]
    G --> H["Never a gamble"]
    B -.->|"For a surrendered devotee"| I["The daivam factor becomes anugraha —\\nthe Lord's personal mercy (see Part 2)"]`,
                caption: 'How endeavor (ceṣṭā) and destiny (daivam) combine in determining the outcome of an activity.',
            },
        },
        {
            type: 'divider',
        },
        {
            type: 'text',
            content:
                'Part 2 continues with why a devotee\'s happiness and distress are not the result of prārabdha-karma at all.',
        },
    ],
};

export default content;
